import {IterationsIcon} from '@primer/octicons-react'
import {ActionList} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useCallback, useMemo} from 'react'

import type {Iteration} from '../../api/columns/contracts/iteration'
import type {MemexProjectColumnId} from '../../api/columns/contracts/memex-column'
import {getIterationLabelType, intervalDatesDescription} from '../../helpers/iterations'
import {isIterationColumnModel} from '../../models/column-model/guards'
import {useAllColumns} from '../../state-providers/columns/use-all-columns'
import {Resources} from '../../strings'
import {CurrentIterationLabel, PlannedIterationLabel} from '../fields/iteration/iteration-label'
import styles from './move-items-to-iteration-menu.module.css'

interface IterationOption {
  iteration: Iteration
  labelType: ReturnType<typeof getIterationLabelType>
}

interface MoveItemsToIterationDialogProps {
  /** The ID of the iteration column to look up fresh data from */
  iterationColumnId: MemexProjectColumnId
  /** The iteration ID of the current group (to exclude from the list) */
  currentIterationId?: string
  /** Callback when user selects a target iteration */
  onSelect: (iteration: Iteration) => void
  /** Callback to close the dialog */
  onClose: () => void
}

export const MoveItemsToIterationDialog = ({
  iterationColumnId,
  currentIterationId,
  onSelect,
  onClose,
}: MoveItemsToIterationDialogProps) => {
  const {allColumns} = useAllColumns()

  const iterationColumn = useMemo(() => {
    const col = allColumns.find(c => c.id === iterationColumnId)
    return col && isIterationColumnModel(col) ? col : undefined
  }, [allColumns, iterationColumnId])

  const iterationOptions = useMemo((): Array<IterationOption> => {
    if (!iterationColumn) return []
    const {configuration} = iterationColumn.settings
    const {iterations = []} = configuration

    return iterations
      .filter(iter => iter.id !== currentIterationId)
      .map(iteration => ({
        iteration,
        labelType: getIterationLabelType(iteration),
      }))
  }, [iterationColumn, currentIterationId])

  const handleSelect = useCallback(
    (iteration: Iteration) => {
      onClose()
      onSelect(iteration)
    },
    [onClose, onSelect],
  )

  return (
    <Dialog
      title={Resources.moveItemsToIterationLabel}
      onClose={onClose}
      width="medium"
      renderBody={() => (
        <Dialog.Body className={styles.dialogBody}>
          <ActionList role="listbox" aria-label={Resources.moveItemsToIterationLabel}>
            {iterationOptions.map(({iteration, labelType}) => (
              <IterationMenuItem
                key={iteration.id}
                iteration={iteration}
                labelType={labelType}
                onSelect={() => handleSelect(iteration)}
              />
            ))}
          </ActionList>
        </Dialog.Body>
      )}
    />
  )
}

interface IterationMenuItemProps {
  iteration: Iteration
  labelType: ReturnType<typeof getIterationLabelType>
  onSelect: () => void
}

const IterationMenuItem = ({iteration, labelType, onSelect}: IterationMenuItemProps) => {
  const dateDescription = intervalDatesDescription(iteration)

  return (
    <ActionList.Item onSelect={onSelect}>
      <ActionList.LeadingVisual>
        <IterationsIcon />
      </ActionList.LeadingVisual>
      {iteration.title}
      {labelType === 'current' && (
        <ActionList.TrailingVisual>
          <CurrentIterationLabel />
        </ActionList.TrailingVisual>
      )}
      {labelType === 'planned' && (
        <ActionList.TrailingVisual>
          <PlannedIterationLabel />
        </ActionList.TrailingVisual>
      )}
      <ActionList.Description variant="block">{dateDescription}</ActionList.Description>
    </ActionList.Item>
  )
}
